// Mini-modal listing a customer's quotes with Open + Archive actions.
// Extracted from QuoteBuilder.jsx (Wave K refactor).
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { GBP } from "../_quoteHelpers";

export default function QuotesPeekModal({ open, email, name, onClose, onOpen }) {
  const [items, setItems] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!email) return;
    setItems(null);
    try {
      const { data } = await api.get(`/quotes?scope=all&search=${encodeURIComponent(email)}&limit=25&sort=updated&include_archived=${showArchived}`);
      setItems(data.items || []);
    } catch { setItems([]); }
  }, [email, showArchived]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const archive = async (row) => {
    const isArchived = row.Status === "Archived";
    if (!isArchived && !window.confirm(`Archive quote ${row.QuoteRef || ""}? It will be hidden from active lists (restore any time).`)) return;
    setBusyId(row._id);
    try {
      await api.post(`/quotes/${row._id}/${isArchived ? "unarchive" : "archive"}`, isArchived ? {} : { reason: "Archived from quotes peek" });
      toast.success(isArchived ? "Quote restored" : "Quote archived");
      await load();
    } catch { toast.error("Action failed"); }
    finally { setBusyId(null); }
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 110, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}
      data-testid="quotes-peek-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(680px, 94vw)", maxHeight: "80vh", background: "#0F1A30",
          border: "1px solid rgba(47,200,203,0.28)", borderRadius: 14, overflow: "hidden",
          boxShadow: "0 30px 80px rgba(0,0,0,.7)", display: "flex", flexDirection: "column",
        }}
        data-testid="quotes-peek-modal"
      >
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--t2-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div>
            <div className="t2-mono" style={{ fontSize: 10.5, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t2-teal-ink)", fontWeight: 800 }}>Quotes for</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "var(--t2-white)" }}>{name}</div>
            <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey3)" }}>{email}</div>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }} className="t2-mono" title="Show archived">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="quotes-peek-show-archived" />
              <span style={{ fontSize: 10, color: "var(--t2-grey2)", textTransform: "uppercase", letterSpacing: ".06em" }}>Archived</span>
            </label>
            <button onClick={onClose} className="t2-btn-ghost" style={{ padding: "4px 8px" }} data-testid="quotes-peek-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="t2-scroll" style={{ overflowY: "auto", padding: 10, flex: 1 }}>
          {items === null && <div className="t2-mono" style={{ padding: 20, color: "var(--t2-grey4)", fontSize: 11 }}>Loading…</div>}
          {items && items.length === 0 && (
            <div className="t2-mono" style={{ padding: 20, color: "var(--t2-grey4)", fontSize: 12, textAlign: "center" }}>
              No quotes yet for this customer.
            </div>
          )}
          {items && items.map((q) => (
            <div key={q._id} className="t2-cmd-row" style={{ gridTemplateColumns: "110px 1fr auto auto", marginBottom: 4 }} data-testid={`quotes-peek-row-${q._id}`}>
              <div className="t2-cmd-sku">{q.QuoteRef}</div>
              <div style={{ overflow: "hidden" }}>
                <div className="t2-cmd-name" style={{ fontSize: 13 }}>{q.Status || "Draft"}</div>
                <div className="t2-cmd-meta">{q.AccountManager || "—"} · {q.Totals?.total != null ? GBP(q.Totals.total) : "—"}</div>
              </div>
              <button
                onClick={() => onOpen(q._id)}
                className="t2-row-action-btn"
                data-testid={`quotes-peek-open-${q._id}`}
              >Open</button>
              <button
                onClick={() => archive(q)}
                disabled={busyId === q._id}
                className={`t2-row-action-btn ${q.Status === "Archived" ? "" : "t2-row-action-btn--danger"}`}
                data-testid={`quotes-peek-archive-${q._id}`}
              >{q.Status === "Archived" ? "Restore" : "Archive"}</button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
